import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { AppHeader } from '../components/AppHeader';
import type { UsageRow } from '../api/types';

function fmtLastRun(row: UsageRow): string {
  if (!row.last_run_at) return '—';
  const when = new Date(row.last_run_at).toLocaleString();
  return row.last_run_status ? `${when} · ${row.last_run_status}` : when;
}

export default function AdminUsage() {
  const usageQ = useQuery({ queryKey: ['admin-usage'], queryFn: api.adminUsage });

  return (
    <div className="min-h-screen bg-ff-paper relative">
      <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" aria-hidden="true" />
      <AppHeader active="usage" />

      <main className="relative mx-auto max-w-[1240px] px-5 pb-24 pt-10 animate-rise sm:px-8 sm:pt-14">
        <div className="mb-10">
          <p className="ff-kicker mb-3">Workspace admin</p>
          <h1 className="ff-page-title text-[clamp(3rem,7vw,4.5rem)] leading-[0.95]">
            Usage <span className="text-ff-orange">overview.</span>
          </h1>
          <p className="mt-4 text-base text-ff-mute">Rendering activity for the trailing 30 days.</p>
        </div>

        {usageQ.isError && (
          <p className="rounded-xl border border-ff-danger/30 bg-ff-danger-soft px-4 py-3 text-sm text-ff-danger">
            Couldn't load usage — {String(usageQ.error)}
          </p>
        )}
        {usageQ.isLoading && <p className="text-sm text-ff-mute">Loading…</p>}

        {usageQ.data && (
          <div className="ff-card overflow-x-auto">
            <table className="min-w-[700px] w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-ff-line bg-ff-paper/80 text-left text-ff-mute">
                  <th className="px-5 py-4 font-semibold">Store</th>
                  <th className="px-4 py-4 text-right font-semibold">Runs</th>
                  <th className="px-4 py-4 text-right font-semibold">Renders (30d)</th>
                  <th className="px-4 py-4 text-right font-semibold">Renders/day</th>
                  <th className="px-4 py-4 font-semibold">Last run</th>
                </tr>
              </thead>
              <tbody>
                {usageQ.data.map((row) => (
                  <tr key={row.store_id} className="border-b border-ff-line/70 last:border-0 hover:bg-ff-mint/10">
                    <td className="px-5 py-4 font-data font-medium">{row.store_id}</td>
                    <td className="px-4 py-4 text-right tabular-nums">{row.n_runs}</td>
                    <td className="px-4 py-4 text-right tabular-nums">{row.renders_30d}</td>
                    <td className="px-4 py-4 text-right tabular-nums">{row.renders_per_day}</td>
                    <td className="px-4 py-4">{fmtLastRun(row)}</td>
                  </tr>
                ))}
                {usageQ.data.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-12 text-center text-ff-mute">
                      No runs in the last 30 days.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
